// src/pages/RegisterPage.tsx

import React, { useState } from 'react';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { userPool } from '../services/cognitoConfig';
import { useNavigate, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import logo from '../assets/images/readLinker_mini_logo_128.png';
import '../css/LoginPage.css';

// LoginPageと同じ、白背景の入力欄をダーク基調のページ上に置くためのスタイル
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 1,
    },
    '& .MuiOutlinedInput-input': {
        color: 'rgba(0, 0, 0, 0.87)',
    },
    '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px white inset',
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
    },
    '& .MuiInputLabel-root': {
        backgroundColor: 'white',
        padding: '0 4px',
        color: 'rgba(0, 0, 0, 0.6)',
        '&.Mui-focused': {
            color: 'rgba(0, 0, 0, 0.6)',
        },
    },
};

const isValidEmail = (email: string): boolean =>
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

// ユーザーIDはそのままCognitoのUsernameとして使う（@付きURLの識別子にもなる）。
// モバイル版と同じ制約（英小文字・数字・_、3〜32文字）
const isValidUsername = (username: string): boolean =>
    /^[a-z0-9_]{3,32}$/.test(username);

type Step = 'email' | 'password';

const RegisterPage: React.FC = () => {
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [usernameError, setUsernameError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();

    // 現在のCognitoユーザープールのパスワードポリシーは「8文字以上」のみ
    // （2026-08確認。ポリシーを変更した場合はここも合わせて更新すること）
    const passwordTooShort = password.length > 0 && password.length < 8;
    const passwordValid = password.length >= 8;

    const handleEmailNext = (e: React.FormEvent) => {
        e.preventDefault();
        let hasError = false;
        if (!isValidEmail(email)) {
            setEmailError('メールアドレスの形式が正しくありません。');
            hasError = true;
        } else {
            setEmailError(null);
        }
        if (!isValidUsername(username)) {
            setUsernameError('ユーザーIDは英小文字・数字・_のみ、3〜32文字で入力してください。');
            hasError = true;
        } else {
            setUsernameError(null);
        }
        if (hasError) return;
        setStep('password');
    };

    const doSignUp = () => {
        const attributes = [
            new CognitoUserAttribute({ Name: 'email', Value: email }),
        ];
        userPool.signUp(username, password, attributes, [], (err, result) => {
            setSubmitting(false);
            if (err) {
                const name = (err as { name?: string }).name;
                if (name === 'InvalidPasswordException') {
                    setSubmitError('パスワードがポリシーを満たしていません。');
                } else if (name === 'UsernameExistsException') {
                    // このユーザーID、またはこのメールアドレスが既に使われている場合の両方でここに来る
                    setStep('email');
                    setUsernameError('このユーザーIDまたはメールアドレスは既に使用されています。別のユーザーIDを指定するか、別のメールアドレスで登録してください。');
                } else {
                    setSubmitError('登録に失敗しました。時間をおいて再試行してください。');
                }
                return;
            }
            // Cognito設定上、メール確認はリンク方式（モバイル版と共通)。
            // userConfirmedがtrueでなければ確認メール送信待ちの状態。
            navigate('/login', {
                state: {
                    message: '登録されたメールに認証リンクを送信しました。メール内のリンクをクリックしてから、ログインしてください。',
                },
            });
        });
    };

    const handleRegister = (e: React.FormEvent) => {
        e.preventDefault();
        if (!passwordValid || submitting) return;
        setSubmitError(null);
        setSubmitting(true);
        doSignUp();
    };

    return (
        <div className="login-page">
            <Container maxWidth="xs">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        color: 'white',
                        py: 20,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <img src={logo} alt="ReadLinker logo" style={{ width: '128px', height: '128px' }} />
                        <Typography component="h1" variant="h5" sx={{ marginTop: 2 }}>
                            アカウントを作成
                        </Typography>

                        {step === 'email' && (
                            <Box component="form" onSubmit={handleEmailNext} sx={{ mt: 3, width: '100%' }}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="username"
                                    label="ユーザーID"
                                    name="username"
                                    autoComplete="username"
                                    autoFocus
                                    value={username}
                                    onChange={(e) => {
                                        const filtered = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 32);
                                        setUsername(filtered);
                                        setUsernameError(null);
                                    }}
                                    inputProps={{ maxLength: 32 }}
                                    sx={fieldSx}
                                />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, fontSize: 12 }}>
                                    半角英小文字・数字・_（アンダースコア）のみ、3〜32文字。プロフィールURLに使われます（例: readlinker.com/{username || 'あなたのID'}）。
                                </Typography>
                                {usernameError && (
                                    <Typography variant="body2" sx={{ color: 'red', mt: 0.5 }}>{usernameError}</Typography>
                                )}
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label="メールアドレス"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                                    sx={fieldSx}
                                />
                                {emailError && (
                                    <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>{emailError}</Typography>
                                )}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 3, mb: 2 }}
                                >
                                    次へ
                                </Button>
                            </Box>
                        )}

                        {step === 'password' && (
                            <Box component="form" onSubmit={handleRegister} sx={{ mt: 3, width: '100%' }}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="password"
                                    label="パスワード"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    autoFocus
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    sx={fieldSx}
                                />
                                {passwordTooShort && (
                                    <Typography variant="body2" sx={{ color: 'red', mt: 0.5, fontSize: 12 }}>
                                        パスワードは8文字以上で入力してください。
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    onClick={() => setShowPassword((v) => !v)}
                                    sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, cursor: 'pointer', textAlign: 'right' }}
                                >
                                    {showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                                </Typography>
                                {submitError && (
                                    <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>{submitError}</Typography>
                                )}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    disabled={!passwordValid || submitting}
                                    sx={{ mt: 3, mb: 1 }}
                                >
                                    {submitting ? '登録中…' : '登録'}
                                </Button>
                                <Button
                                    fullWidth
                                    onClick={() => { setStep('email'); setSubmitError(null); }}
                                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                                >
                                    戻る
                                </Button>
                            </Box>
                        )}

                        <Typography variant="body2" sx={{ marginTop: 2 }}>
                            すでにアカウントをお持ちの方は<Link to="/login" style={{ color: 'white' }}>こちら</Link>
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                        © 2023-2026 ADA LLC.
                    </Typography>
                </Box>
            </Container>
        </div>
    );
};

export default RegisterPage;
